<template>
  <div
    class="icon-badge"
    :class="{ 'icon-badge--fluid': fluid }"
    v-b-tooltip.topright.viewport
    :title="tooltipMessage"
    :style="iconStyle"
    @click="onClickIcon"
  >
    <slot>
      <i aria-hidden="true"
        :class="iconClass"
        :style="{ color: iconColor }"
      />
    </slot>
    <b-badge
      v-if="showBadge"
      :style="badgeStyle"
      class="icon-badge__badge"
    >
      <i
        aria-hidden="true"
        :class="badgeIconClass"
        :style="badgeStyle"
      />
    </b-badge>
  </div>
</template>

<script>
export default {
  name: 'IconBadge',
  props: {
    iconColor: {
      type: String,
      required: true,
    },
    iconClass: {
      type: String,
      required: true,
    },
    badgeIconClass: {
      type: String,
      default: '',
    },
    showBadge: {
      type: Boolean,
      default: true,
    },
    badgeColor: {
      type: String,
      default: '',
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    /**
     * Faz o selo acompanhar a fonte de quem o contém, em vez de valer 25px fixos.
     *
     * Vem desligado de propósito. Ligado por default, todo consumidor que vive
     * dentro de um contexto com fonte reduzida encolheria sem ninguém pedir — é o
     * caso de `InstallationAlarmPanel`, cuja `s-table` roda a `.83rem` e faria o
     * selo cair de 25px para 20.75px em `/alarmes-e-avisos`. Quem quer a escala
     * pede por ela.
     */
    fluid: {
      type: Boolean,
      default: false,
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    tooltipMessage: {
      type: String,
      default: '',
    },
  },
  computed: {
    badgeStyle() {
      return {
        color: this.badgeColor || this.iconColor,
        'background-color': 'transparent',
      };
    },
    iconStyle() {
      if (!this.outlined) return {};

      // `max()` porque a borda tem dois deveres em conflito: num formulário ela é
      // um fio de 1px, e 0.0625em daria menos que isso se alguém reduzir a fonte
      // — sub-pixel some em metade dos monitores. Numa TV de parede, 1px fixo é
      // que desaparece. O `max` atende os dois: 1px é o piso, o `em` é o que
      // cresce. Em 16px os dois valem 1px, então nada muda onde já estava certo.
      return {
        border: `max(1px, 0.0625em) solid ${this.iconColor}`,
      };
    },
  },
  methods: {
    onClickIcon() {
      this.$emit('click');
    },
  },
};
</script>

<style lang="sass" scoped>
  // As medidas saíram de `px` para `em`, todas ancoradas na `font-size` do próprio
  // selo. Sozinho isso não muda nada: com `font-size: 1rem`, `1.5625em` dá os
  // mesmos 25px de antes. O que muda é que agora existe um ponto único de onde
  // escalar o desenho inteiro — e `--fluid` é quem mexe nele.
  //
  // Os valores preservam o desenho ao pé da letra: 25px / 16px = 1.5625em, e não
  // 1.5em.
  .icon-badge
    font-size: 1rem
    display: inline-flex
    align-items: center
    justify-content: center
    padding: .2em .45em
    border-radius: 0.3125em
    width: 1.5625em
    height: 1.5625em
    position: relative

    // O `em` daqui mede a fonte **do próprio selo** (1.1em do pai), e não a do
    // pai: por isso 0.6818em e não 0.75em. Como a fonte do selo escala com a do
    // pai, o deslocamento escala junto — que é o que mantém o selo no canto.
    &__badge
      position: absolute
      transform: translate(0.6818em, -0.3977em)
      font-size: 1.1em

  // A única linha que a escala precisa: `1em` herda a fonte do contexto, e as
  // outras medidas, sendo `em`, vão atrás. Num painel de TV de parede, onde o
  // container query aumenta a fonte, o selo cresce junto com o texto ao lado em
  // vez de virar um carimbo minúsculo no canto.
  .icon-badge--fluid
    font-size: 1em
</style>
